import * as Sentry from "@sentry/react";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { apiGet, queryClient } from "./api";
import AcceptInvite from "./components/AcceptInvite";
import AccountPage from "./components/AccountPage";
import HomePage from "./components/HomePage";
import InfoMessage from "./components/InfoMessage";
import Loading from "./components/Loading";
import Login from "./components/Login";
import Logout from "./components/Logout";
import NotFound from "./components/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import SubmissionPage from "./components/SubmissionPage";
import TasksPage from "./components/TasksPage";
import Signup from "./components/Signup";
import ConfirmEmail from "./components/ConfirmEmail";
import ConfirmEmailDone from "./components/ConfirmEmailDone";
import { UserContext } from "./contexts";

export default function App() {
  return (
    <div className="flex flex-col min-h-screen">
      <Sentry.ErrorBoundary
        fallback={<InfoMessage>Sorry! Something went wrong</InfoMessage>}
      >
        <QueryClientProvider client={queryClient}>
          <Main />
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </div>
  );
}

function ErrorTrigger() {
  throw new Error("Test Error");
}

function Main() {
  const { data: user, isLoading: userIsLoading } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      try {
        const { data } = await apiGet("user");
        return data;
      } catch (error) {
        return null;
      }
    },
    retry: false,
  });

  if (userIsLoading) {
    return <Loading />;
  }

  return (
    <UserContext.Provider value={{ user }}>
      <Router>
        <div className="flex-1 flex flex-col">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/signup" element={<Signup />} />
            <Route
              path="/confirm-email/:userId/:token"
              element={<ConfirmEmail />}
            />
            <Route path="/confirm-email/done" element={<ConfirmEmailDone />} />
            <Route path="/accept-invite/:code" element={<AcceptInvite />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <TasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/submission/:submissionId"
              element={
                <ProtectedRoute>
                  <SubmissionPage />
                </ProtectedRoute>
              }
            />
            <Route path="/trigger-js-error" element={<ErrorTrigger />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Router>
    </UserContext.Provider>
  );
}
